import type { ReactNode } from 'react';
import './lista-linha.css';

export type ListaLinhaProps = {
  /** O disco de iniciais, ou nada. */
  inicio?: ReactNode;
  /** Nome, e o que vier junto na primeira linha (selo de exceção). */
  nome: ReactNode;
  /** Segunda linha: prévia da mensagem, contato mascarado. */
  previa?: ReactNode;
  /** Terceira linha: etapa, valor, etiquetas — em texto neutro. */
  meta?: ReactNode;
  /** Canto direito: hora e contador. */
  direita?: ReactNode;
  /**
   * A direita tem BOTÃO, e não metadado.
   *
   * No celular o slot da direita desce para baixo do texto, o que lê bem
   * quando ali vai hora ou contador de não lidas. Com botão não lê: dois alvos
   * de 44px empilhados sob o nome levam a linha de ~60px para 132px — quatro
   * clientes visíveis onde a variante densa promete doze —, e a faixa de ações
   * passa a mandar na largura da primeira coluna, abrindo um buraco entre cada
   * avatar e o nome dele, lista abaixo.
   *
   * É prop e não o padrão porque o colapso foi decidido para a fila de
   * conversas: invertê-lo mudaria a fila em silêncio.
   */
  controles?: boolean;
  /**
   * Controles que ficam FORA do alvo da linha — botão, menu de ações.
   *
   * Botão dentro de botão é HTML inválido, e o leitor de tela anuncia um alvo
   * só: quem navega por teclado nunca alcança o menu. Com `acoes`, a linha
   * deixa de ser um `<button>` e passa a ser uma caixa com um alvo SOBREPOSTO
   * atrás do conteúdo — a linha inteira continua clicável, e os controles
   * ficam por cima dele, cada um com o próprio foco.
   *
   * `rotulo` vira obrigatório na prática: o alvo sobreposto é vazio, e sem
   * rótulo ele é um botão sem nome.
   */
  acoes?: ReactNode;
  selecionada?: boolean;
  naoLida?: boolean;
  densa?: boolean;
  aoAbrir?: () => void;
  /** Rótulo do alvo, quando o conteúdo visível não basta. */
  rotulo?: string;
};

/**
 * A linha inteira é o alvo, e por isso é um `<button>`.
 *
 * Um link no nome com o resto inerte faz a pessoa acertar o alvo em um de cada
 * três cliques numa lista densa — e é a tela em que ela clica o dia inteiro.
 */
export function ListaLinha({
  inicio,
  nome,
  previa,
  meta,
  direita,
  acoes,
  controles = false,
  selecionada = false,
  naoLida = false,
  densa = false,
  aoAbrir,
  rotulo,
}: ListaLinhaProps) {
  const classe = [
    'lista-linha',
    densa ? 'lista-linha--densa' : null,
    controles ? 'lista-linha--controles' : null,
  ]
    .filter(Boolean)
    .join(' ');

  const corpo = (
    <>
      {inicio}
      <span className="lista-linha__corpo">
        <span className="lista-linha__topo">{nome}</span>
        {previa ? <span className="lista-linha__previa">{previa}</span> : null}
        {meta ? <span className="lista-linha__meta">{meta}</span> : null}
      </span>
      {(direita ?? acoes) ? (
        <span className="lista-linha__direita">
          {direita}
          {acoes}
        </span>
      ) : null}
    </>
  );

  /*
   * Com controles, o alvo é sobreposto — ver a prop `acoes`.
   *
   * O `<button>` vazio ocupa a linha inteira por baixo do conteúdo; os
   * controles sobem de camada e continuam alcançáveis pelo dedo e pelo Tab. O
   * anel de foco desenha em volta da linha porque o alvo TEM o tamanho da
   * linha, e não porque alguém o desenhou de novo.
   */
  if (acoes) {
    return (
      <li>
        <div
          className={classe}
          aria-current={selecionada ? true : undefined}
          data-nao-lida={naoLida ? 'sim' : undefined}
        >
          <button
            type="button"
            className="lista-linha__alvo"
            onClick={aoAbrir}
            aria-label={rotulo}
          />
          {corpo}
        </div>
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        className={classe}
        aria-current={selecionada ? true : undefined}
        data-nao-lida={naoLida ? 'sim' : undefined}
        onClick={aoAbrir}
        aria-label={rotulo}
      >
        {corpo}
      </button>
    </li>
  );
}

export type ListaProps = {
  /** Régua interna mais fraca, para lista longa de itens iguais. */
  fina?: boolean;
  children?: ReactNode;
};

export function Lista({ fina = false, children }: ListaProps) {
  return <ul className={fina ? 'lista lista--fina' : 'lista'}>{children}</ul>;
}
